import { GraphInterrupt, InterruptChoice, InterruptKind } from './types';

const INTERRUPT_KINDS: readonly InterruptKind[] = [
  'missing-data',
  'source-conflict',
  'staged-files',
  'approval',
  'generic',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asKind(value: unknown): InterruptKind | undefined {
  return typeof value === 'string' && (INTERRUPT_KINDS as readonly string[]).includes(value)
    ? (value as InterruptKind)
    : undefined;
}

function hasArray(value: Record<string, unknown>, key: string): boolean {
  return Array.isArray(value[key]);
}

function isMissingDataPayload(value: Record<string, unknown>): boolean {
  return (
    hasArray(value, 'missing_profile') ||
    hasArray(value, 'missing_domains') ||
    hasArray(value, 'missing_profile_labels') ||
    hasArray(value, 'missing_domain_labels')
  );
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function isSourceConflictPayload(value: Record<string, unknown>): boolean {
  return (
    typeof value.conflict_id === 'string' &&
    typeof value.resolution_token === 'string' &&
    Array.isArray(value.candidates)
  );
}

function formatCandidateValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Resolve the interrupt kind. Prefer an explicit `payload.kind`; fall back to the
 * PlanningInterrupt `type` (the 2712 parser carries a semantic type string there);
 * otherwise render the generic control.
 */
function resolveKind(payloadKind: unknown, interruptType: unknown): InterruptKind {
  if (payloadKind === 'staged_files_gate') {
    return 'staged-files';
  }
  return asKind(payloadKind) ?? asKind(interruptType) ?? 'generic';
}

function normalizeOptions(value: unknown): InterruptChoice[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const options = value
    .filter(isRecord)
    .filter((option) => typeof option.id === 'string' && typeof option.label === 'string')
    .map((option) => ({
      id: option.id as string,
      label: option.label as string,
      value: option.value,
    }));
  return options.length > 0 ? options : undefined;
}

function normalizeSourceConflictOptions(value: unknown): InterruptChoice[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const options = value.flatMap((candidate): InterruptChoice[] => {
    if (!isRecord(candidate) || typeof candidate.claim_id !== 'string') {
      return [];
    }

    const formattedValue = formatCandidateValue(candidate.value);
    const sourceLabel =
      typeof candidate.source_label === 'string' && candidate.source_label
        ? candidate.source_label
        : 'unknown';

    return [
      {
        id: candidate.claim_id,
        label: formattedValue,
        value: candidate.value,
        sourceLabel,
      },
    ];
  });

  return options.length > 0 ? options : undefined;
}

function normalizeQuestion(payload: Record<string, unknown>, kind: InterruptKind): string {
  if (typeof payload.question === 'string' && payload.question.trim()) {
    return payload.question;
  }

  if (kind === 'staged-files' && typeof payload.message === 'string') {
    return payload.message;
  }

  if (kind === 'source-conflict') {
    const fieldLabel = typeof payload.field_label === 'string' ? payload.field_label.trim() : '';
    return fieldLabel ? `Choose the value to use for ${fieldLabel}.` : 'Choose the value to use.';
  }

  if (kind === 'missing-data') {
    const missing = [
      ...stringArray(payload.missing_profile_labels),
      ...stringArray(payload.missing_domain_labels),
    ];

    if (missing.length > 0) {
      return `Please provide the missing details: ${missing.join(', ')}.`;
    }
    return 'Please provide the missing details.';
  }

  return '';
}

function resolvePayloadId(
  payload: Record<string, unknown>,
  kind: InterruptKind,
): string | undefined {
  const id = payload.id;
  if (typeof id === 'string' && id) return id;

  if (kind === 'source-conflict' && typeof payload.conflict_id === 'string') {
    return payload.conflict_id;
  }

  if (kind === 'missing-data' || payload.kind === 'staged_files_gate') {
    return typeof payload.token === 'string' && payload.token ? payload.token : undefined;
  }

  return undefined;
}

function resolvePayloadToken(
  payload: Record<string, unknown>,
  kind: InterruptKind,
): string | undefined {
  const token =
    kind === 'source-conflict' ? (payload.resolution_token ?? payload.token) : payload.token;
  return typeof token === 'string' && token ? token : undefined;
}

function normalizeDetails(
  payload: Record<string, unknown>,
  kind: InterruptKind,
): Record<string, unknown> | undefined {
  const details = isRecord(payload.details) ? { ...payload.details } : {};

  if (kind === 'missing-data') {
    if (Array.isArray(payload.missing_profile)) details.missing_profile = payload.missing_profile;
    if (Array.isArray(payload.missing_domains)) details.missing_domains = payload.missing_domains;
    if (Array.isArray(payload.already_have)) details.already_have = payload.already_have;
    if (Array.isArray(payload.missing_profile_labels)) {
      details.missing_profile_labels = payload.missing_profile_labels;
    }
    if (Array.isArray(payload.missing_domain_labels)) {
      details.missing_domain_labels = payload.missing_domain_labels;
    }
    if (Array.isArray(payload.already_have_labels)) {
      details.already_have_labels = payload.already_have_labels;
    }
  }

  if (kind === 'source-conflict') {
    if (typeof payload.conflict_id === 'string') details.conflict_id = payload.conflict_id;
    if (typeof payload.conflict_signature === 'string') {
      details.conflict_signature = payload.conflict_signature;
    }
    if (typeof payload.field_path === 'string') details.field_path = payload.field_path;
    if (typeof payload.field_label === 'string') details.field_label = payload.field_label;
  }

  if (kind === 'staged-files' && typeof payload.file_count === 'number') {
    details.file_count = payload.file_count;
  }

  return Object.keys(details).length > 0 ? details : undefined;
}

/**
 * Adapt the interrupt emitted by the ENG-2712 parser — a PlanningInterrupt of the
 * form `{ type, payload }` — into our internal {@link GraphInterrupt}.
 *
 * This is the ONLY place in the frontend that touches the parser's shape. The card,
 * resume builder, slice, and tests all stay on GraphInterrupt, so when the backend
 * payload contract lands the reconciliation happens HERE and nowhere else.
 *
 * The interrupt fields are read from `payload`; `type` is used as a fallback for
 * `kind`. Returns null when we can't resume (no token) — the caller surfaces a
 * graceful message.
 *
 * TODO(ENG-2714): confirm the inner `payload` field paths (id, token, question,
 * options) and where `kind` lives (payload.kind vs the PlanningInterrupt `type`).
 * ENG-2712 does NOT freeze these — its parser passes `payload` through opaquely and
 * its test fixtures use illustrative shapes (`{ question }`, `{ options: ['A','B'] }`,
 * `{}`) with no id/token. Adjust the reads in this function only, once the ENG-2714
 * backend emission is merged and frozen.
 */
export function mapParsedInterruptToGraphInterrupt(parsed: unknown): GraphInterrupt | null {
  if (!isRecord(parsed)) {
    return null;
  }

  // Accept a full PlanningInterrupt ({ type, payload }); tolerate a bare payload
  // object too, so callers/tests can pass either.
  const payload = isRecord(parsed.payload) ? parsed.payload : parsed;

  const inferredKind = isMissingDataPayload(payload)
    ? 'missing-data'
    : isSourceConflictPayload(payload)
      ? 'source-conflict'
      : undefined;
  const kind = inferredKind ?? resolveKind(payload.kind, parsed.type);
  const id = resolvePayloadId(payload, kind);
  const token = resolvePayloadToken(payload, kind);
  if (!id || !token) {
    return null;
  }

  return {
    id,
    token,
    kind,
    question: normalizeQuestion(payload, kind),
    options:
      kind === 'source-conflict'
        ? (normalizeSourceConflictOptions(payload.candidates) ?? normalizeOptions(payload.options))
        : normalizeOptions(payload.options),
    details: normalizeDetails(payload, kind),
    checkpoint: isRecord(payload.checkpoint) ? payload.checkpoint : undefined,
  };
}
